import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useStore } from "zustand";
import "./style.scss";
import { PATH_LIST } from "shared/lib/react-router";
import { AssetType, OperationType } from "static/types";
import { GetCurrByCode } from "helpers/currencies";
import { ISpecialOffer, marketStore } from "entities/market";
import { dashboardStore } from "entities/dashboard";
import { useBlockScrollbar } from "hooks/useBlockScrollbar";
import SwitchTabs from "widgets/switch-tabs";
import { AssetsContainer } from "../home";
import CurrencyCard from "../components/currency-card";
import PriceCard from "../components/currency-price-block";
import { VerificationBtn } from "../verification-steps/components/button";
import Alert from "../../../assets/Alert.svg";
import { ReactComponent as TopUpIcon } from "../../../assets/TopUpPink.svg";
import Show from "../../../assets/ShowBalance.svg";
import Hide from "../../../assets/HideBalance.svg";
import { ReactComponent as CurrencyDownIcon } from "../../../assets/CurrencyDown.svg";
import { fxOrderStore } from "entities/fx-order";
import Modal from "widgets/auth/modal";
import HorizontalScroll from "../components/horizontal-scroll";
import SpecialOfferCard from "../components/special-offer";
import useCountdown from "../../../hooks/useCountdown";
import { accountStore } from "entities/account";
import { Transaction } from "../components/activities-transactions";
import EmptyOrdersBox from "../components/empty-orders-box";
import { useVerificationNavigation } from "hooks/useNavigateVerification";
import { toast } from "react-toastify";
import { userVerified } from "helpers/functions";

type CapableCurrency = {
  code: string;
  name: string;
  symbol: string;
  bid: number;
  ask: number;
  type: string;
  assetID: string;
};

type UnifiedCurrency = {
  amount: number;
  capable: CapableCurrency[];
  code: string;
  name: string;
  symbol: string;
};

const IS_DEV = process.env.REACT_APP_DEV_MODE === "true";

export const CurrenciesPage = () => {
  const { t, i18n } = useTranslation("app");
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    location.state?.currencyTab || "exchange",
  );
  const [hide, setHide] = useState(false);
  const [openSheetIndex, setOpenSheetIndex] = useState<number | null>(null);
  const [transactionSheetOpen, setTransactionSheetOpen] = useState(false);
  const block = openSheetIndex !== null || transactionSheetOpen;
  useBlockScrollbar(block, [block]);
  const {
    isLoading,
    loadAssets,
    assetsCurrencies,
    specialOffers,
    loadSpecialOffers,
  } = useStore(marketStore);
  const {
    from,
    to,
    operation,
    quantity,
    limitPrice,
    change_type,
    setChangeType,
    setFrom,
    setTo,
    setOperation,
    setQuantity,
    setLimitPrice,
    setAmount,
    amount,
    selectedOfferID,
    setSpecialOfferID: setSelectedOfferID,
    clear,
  } = useStore(fxOrderStore);
  const { user_deposit_accounts, loadUserAccounts } = useStore(dashboardStore);
  const { status } = useVerificationNavigation();

  // const [limitPriceInvalid, setLimitPriceInvalid] = useState(false);
  // const [limitPriceChecked, setLimitPriceChecked] = useState(false);
  const [infoModal, setInfoModal] = useState(false);
  const [activeOrderTab, setActiveOrderTab] = useState("active");

  // * Countdown for selected offer
  const selectedOffer =
    selectedOfferID !== null
      ? specialOffers.find((s) => s.id === selectedOfferID)
      : null;
  const selectedOfferCountdown = useCountdown({
    validFrom: selectedOffer ? new Date(selectedOffer.valid_from) : new Date(),
    validTo: selectedOffer ? new Date(selectedOffer.valid_to) : new Date(),
  });

  const volumeFrom = selectedOffer
    ? +selectedOffer.volume_from?.toFixed(2) || 0
    : 0;
  const volumeTo = selectedOffer
    ? +selectedOffer?.volume_to?.toFixed(2) || 0
    : 0;

  const timeLeftForPriceCard = selectedOffer ? selectedOfferCountdown : "";

  const {
    transactions,
    transactionsCount,
    activeTransactions,
    getTransactions,
    getActiveTransactions,
  } = useStore(accountStore);

  const handleInfoClick = () => {
    setInfoModal(true);
  };

  const handleTransactionSheetToggle = (isOpen: boolean) => {
    setTransactionSheetOpen(isOpen);
  };

  useEffect(() => {
    // clear();
    loadSpecialOffers();
    loadUserAccounts();
    loadAssets({
      type: String(AssetType.Currency),
      page: 0,
      limit: 0,
    });
  }, []);

  const getUserAccount = useMemo(() => {
    return user_deposit_accounts.map((acc) => {
      return {
        symbol: acc.currency.code,
        amount: acc.amount,
      };
    });
  }, [user_deposit_accounts]);

  const unifiedCurrencies: UnifiedCurrency[] = useMemo(() => {
    if (!Array.isArray(assetsCurrencies)) return [];

    const exchangeMap = new Map<string, Set<string>>();
    assetsCurrencies.forEach((item) => {
      if (!item.symbol || !item.currency?.code) return;
      if (!exchangeMap.has(item.symbol))
        exchangeMap.set(item.symbol, new Set());
      exchangeMap.get(item.symbol)?.add(item.currency.code);

      if (!exchangeMap.has(item.currency.code))
        exchangeMap.set(item.currency.code, new Set());
      exchangeMap.get(item.currency.code)?.add(item.symbol);
    });

    return Array.from(exchangeMap.keys())
      .map((code) => {
        const baseCurrency = GetCurrByCode(code);
        if (!baseCurrency) return null;

        const capableCodes = exchangeMap.get(code) || new Set();

        const capableCurrencies: CapableCurrency[] = [];
        capableCodes.forEach((targetCode) => {
          // Try direct pair
          const directPair = assetsCurrencies.find(
            (item) =>
              item.symbol === code && item.currency?.code === targetCode,
          );
          if (directPair) {
            capableCurrencies.push({
              code: directPair.currency.code,
              name:
                GetCurrByCode(directPair.currency.code)?.name ||
                directPair.currency.code,
              symbol: directPair.currency.symbol,
              bid: directPair.bid || 0,
              ask: directPair.ask || 0,
              type: "direct",
              assetID: directPair.id || "",
            });
          } else {
            // fallback reverse pair
            const reversePair = assetsCurrencies.find(
              (item) =>
                item.symbol === targetCode && item.currency?.code === code,
            );
            if (reversePair) {
              capableCurrencies.push({
                code: reversePair.symbol,
                name:
                  GetCurrByCode(reversePair.symbol)?.name || reversePair.symbol,
                symbol: GetCurrByCode(reversePair.symbol)?.symbol || "",
                bid: reversePair.bid,
                ask: reversePair.ask,
                type: "reverse",
                assetID: reversePair.id || "",
              });
            }
          }
        });

        return {
          amount:
            getUserAccount.find((acc) => acc.symbol === code)?.amount || 0,
          capable: capableCurrencies,
          code: baseCurrency.code,
          name: baseCurrency.name,
          symbol: baseCurrency.symbol,
        };
      })
      .filter(Boolean) as UnifiedCurrency[];
  }, [assetsCurrencies]);

  const buyCurrencyOptions = useMemo(() => {
    const sellCurrency = unifiedCurrencies.find((item) => item.code === from);
    if (!sellCurrency || !sellCurrency.capable) return [];
    return unifiedCurrencies.filter((item) =>
      sellCurrency.capable.some((capable) => capable.code === item.code),
    );
  }, [unifiedCurrencies, from]);

  const selectedPair = useMemo(() => {
    const sellCurrency = unifiedCurrencies.find((item) => item.code === from);
    if (!sellCurrency) return null;

    const pair = sellCurrency.capable.find((item) => item.code === to);
    if (!pair) return null;

    return {
      baseSymbol: pair.type === "direct" ? sellCurrency.symbol : pair.symbol,
      symbol: pair.type === "direct" ? pair.symbol : sellCurrency.symbol,
      bid: pair.bid,
      ask: pair.ask,
      assetID: pair.assetID,
      type: pair.type,
    };
  }, [unifiedCurrencies, from, to]);

  const isDirect = selectedPair?.type === "direct";

  useEffect(() => {
    if (buyCurrencyOptions.length > 0) {
      if (!to || !buyCurrencyOptions.some((opt) => opt.code === to)) {
        setTo(buyCurrencyOptions[0].code);
      }
    }
  }, [buyCurrencyOptions, from, to]);

  useEffect(() => {
    if (selectedPair && !selectedOfferID) {
      setOperation(
        selectedPair.type === "direct" ? OperationType.Sell : OperationType.Buy,
      );
    }
  }, [selectedPair, selectedOfferID]);

  const currentAmount =
    getUserAccount.find((acc) => acc.symbol === from)?.amount || 0;

  const currentSymbol =
    unifiedCurrencies.find((item) => item.code === from)?.symbol || "";

  const marketPrice = selectedPair
    ? selectedPair.type === "direct"
      ? selectedPair.bid
      : selectedPair.ask
    : 0;

  // useEffect(() => {
  //   console.log(
  //     transactions
  //       .filter((t) => t.asset.type === AssetType.Currency)
  //       .map((t) => t.asset.type)
  //   );
  //   console.log(
  //     activeTransactions
  //       .filter((t) => t.asset.type === AssetType.Currency)
  //       .map((t) => t.asset.type)
  //   );
  // }, [transactions, activeTransactions]);

  const price = useMemo(() => {
    if (limitPrice && limitPrice > 0) {
      return limitPrice;
    }

    return marketPrice;
  }, [assetsCurrencies, from, to, limitPrice, marketPrice]);

  useEffect(() => {
    if (selectedPair) {
      if (operation === OperationType.Sell) {
        if (change_type === "quantity") {
          setAmount(quantity === "" ? "" : +(quantity * price).toFixed(4));
        } else {
          setQuantity(amount === "" ? "" : +(amount / price).toFixed(4));
        }
      } else {
        if (change_type === "quantity") {
          setAmount(quantity === "" ? "" : +(quantity * price).toFixed(4));
        } else {
          setQuantity(amount === "" ? "" : +(amount / price).toFixed(4));
        }
      }
    }
  }, [selectedPair, operation, price, amount, change_type, quantity]);

  const tabOptions = [
    { key: "exchange", label: "exchange2" },
    { key: "trading", label: "trading" },
    { key: "orders", label: "orders" },
  ];

  const handleReview = () => {
    if (!selectedPair) return;

    navigate(`${PATH_LIST.currencyOrder(selectedPair.assetID)}`);
  };

  const handleSwapCurrencies = () => {
    if (change_type === "quantity") {
      setAmount(0);
      setChangeType("quantity");
    } else {
      setQuantity(0);
      setChangeType("amount");
    }
    setFrom(to);
    setTo(from);
    if (selectedOfferID) {
      setLimitPrice(null);
      setSelectedOfferID(null);
    }
  };

  const handleSpecialOffer = (offer: ISpecialOffer) => {
    setAmount("");
    setQuantity("");
    setFrom(
      offer.operation === OperationType.Buy ? offer.currency : offer.symbol,
    );
    setTo(
      offer.operation === OperationType.Buy ? offer.symbol : offer.currency,
    );
    const isSelected = selectedOfferID === offer.id;
    const newOffer = specialOffers.find((s) => s.id === offer.id);
    if (!isSelected) {
      setOperation(newOffer!.operation);
      setLimitPrice(newOffer?.price || null);
      setSelectedOfferID(offer.id);
    } else {
      setLimitPrice(null);
      setSelectedOfferID(null);
    }
  };

  const volumeValidation = useMemo(() => {
    if (!selectedOffer || quantity === "" || amount === "") {
      return true;
    }

    const compareValue =
      (selectedOffer.volume_currency === from && isDirect) ||
      (selectedOffer.volume_currency === to && !isDirect)
        ? quantity
        : amount;

    return +compareValue >= volumeFrom && +compareValue <= +volumeTo;
  }, [isDirect, quantity, amount, volumeFrom, volumeTo, selectedOffer, from]);

  useEffect(() => {
    getActiveTransactions(0, 0);
    getTransactions(0, 0);
  }, []);

  const activeTransactionsCurrencies = useMemo(() => {
    return activeTransactions.filter(
      (t) => t.asset.type === AssetType.Currency,
    );
  }, [activeTransactions]);

  const transactionsCurrencies = useMemo(() => {
    return transactions.filter((t) => t.asset.type === AssetType.Currency);
  }, [transactions]);

  // console.log(activeTransactionsCurrencies);
  // console.log(transactionsCurrencies);

  const [page, setPage] = useState(0);
  const limit = 20;

  const currItemsLength = (page + 1) * limit;
  const count = transactionsCount;

  return (
    <div className="currencies-nav-page">
      <SwitchTabs
        tabs={tabOptions}
        activeTabKey={activeTab}
        onClick={setActiveTab}
      />

      {activeTab === "exchange" ? (
        <div
          key="exchange"
          className="currencies-nav-page-exchange"
          style={{ paddingBottom: volumeValidation ? "78px" : "142px" }}
        >
          {specialOffers && specialOffers.length > 0 && (
            <HorizontalScroll className="currencies-exchange-special-offer">
              {specialOffers.map((offer) => {
                return (
                  <SpecialOfferCard
                    key={offer.id}
                    symbol={offer.currency}
                    currency={{
                      from:
                        offer.operation === OperationType.Buy
                          ? offer.currency
                          : offer.symbol,
                      to:
                        offer.operation === OperationType.Buy
                          ? offer.symbol
                          : offer.currency,
                      symbol: offer.volume_currency,
                    }}
                    valid={{
                      from: new Date(offer.valid_from),
                      to: new Date(offer.valid_to),
                    }}
                    price={offer.price}
                    volume={{
                      from: offer.volume_from,
                      to: offer.volume_to,
                    }}
                    selected={selectedOfferID === offer.id}
                    onClick={() => handleSpecialOffer(offer)}
                  />
                );
              })}
            </HorizontalScroll>
          )}

          <div key="orders" className="currencies-exchange-header">
            <p
              className="currencies-exchange-sell"
              style={{
                fontSize: ["am", "ru"].includes(i18n.language)
                  ? "24px"
                  : "32px",
                lineHeight: ["am", "ru"].includes(i18n.language)
                  ? "28px"
                  : "36px",
              }}
            >
              {t("sell")} {from}
            </p>

            <div className="currencies-exchange-right">
              <span onClick={() => setHide(!hide)}>{t("available")}</span>
              <p onClick={() => setHide(!hide)}>
                <img src={hide ? Hide : Show} alt="Show" />
                {hide ? "•••••" : +currentAmount.toFixed(2)}
                {currentSymbol}
              </p>
              {userVerified() && (
                <TopUpIcon
                  role="button"
                  aria-label={t("topUpAccount")}
                  onClick={() =>
                    status
                      ? toast.error(t("verificationRequired"))
                      : navigate(PATH_LIST.deposit, {
                          state: { currency: from },
                        })
                  }
                />
              )}
            </div>
          </div>

          <div className="currency-cards">
            <CurrencyCard
              data={unifiedCurrencies}
              selectedCode={from}
              onSelect={(code) => {
                if (code === to) {
                  handleSwapCurrencies();
                  return;
                }
                const fromCurrency = unifiedCurrencies.find(
                  (item) => item.code === code,
                )!;
                const toCurrency = fromCurrency.capable.find(
                  (item) => item.code === to,
                );
                if (toCurrency) {
                  setAmount(0);
                  setChangeType(
                    toCurrency.type === "direct" ? "quantity" : "amount",
                  );
                } else {
                  setQuantity(0);
                  setAmount(0);
                }
                setFrom(code);
                if (selectedOfferID) {
                  setLimitPrice(null);
                  setSelectedOfferID(null);
                }
              }}
              onSheetToggle={(isOpen) => setOpenSheetIndex(isOpen ? 0 : null)}
              isSheetOpen={openSheetIndex === 0}
              amount={isDirect ? quantity : amount}
              setAmount={(val) => {
                if (isDirect) {
                  setQuantity(val);
                  setChangeType("quantity");
                } else {
                  setAmount(val);
                  setChangeType("amount");
                }
              }}
              hide={hide}
              sign="-"
              // sign="—"
              placeholder={
                selectedOfferID !== null &&
                selectedOffer?.volume_currency === from
                  ? `${volumeFrom} - ${volumeTo}`
                  : undefined
              }
              validation={!volumeValidation}
            />

            <button
              className="currency-down-btn"
              onClick={handleSwapCurrencies}
            >
              <CurrencyDownIcon aria-hidden />
            </button>

            <CurrencyCard
              data={buyCurrencyOptions}
              selectedCode={to}
              onSelect={(code) => {
                if (code === from) {
                  handleSwapCurrencies();
                  return;
                }
                const fromCurrency = unifiedCurrencies.find(
                  (item) => item.code === from,
                )!;
                const toCurrency = fromCurrency.capable.find(
                  (item) => item.code === code,
                )!;
                if (toCurrency.type === "reverse") {
                  if (selectedPair?.type === "direct") {
                    setAmount(quantity);
                  }
                  setChangeType("amount");
                } else if (selectedPair?.type === "direct") {
                  setAmount(0);
                  setChangeType("quantity");
                } else {
                  setQuantity(amount);
                  setChangeType("quantity");
                }
                setTo(code);

                if (selectedOfferID) {
                  setLimitPrice(null);
                  setSelectedOfferID(null);
                }
              }}
              onSheetToggle={(isOpen) => setOpenSheetIndex(isOpen ? 1 : null)}
              isSheetOpen={openSheetIndex === 1}
              amount={isDirect ? amount : quantity}
              setAmount={(val) => {
                if (isDirect) {
                  setAmount(val);
                  setChangeType("amount");
                } else {
                  setQuantity(val);
                  setChangeType("quantity");
                }
              }}
              hide={hide}
              sign="+"
              placeholder={
                selectedOfferID !== null &&
                selectedOffer?.volume_currency === to
                  ? `${volumeFrom} - ${volumeTo}`
                  : undefined
              }
            />
          </div>

          <PriceCard
            marketPrice={marketPrice}
            sellCurrencySymbol={selectedPair?.baseSymbol || ""}
            buyCurrencySymbol={selectedPair?.symbol || ""}
            value={limitPrice}
            onInfoClick={handleInfoClick}
            setValue={(val) => {
              setLimitPrice(val);
              setSelectedOfferID(null);
            }}
            bid={selectedPair?.bid}
            ask={selectedPair?.ask}
            specialOffer={
              selectedOfferID !== null && selectedOffer
                ? {
                    from: selectedOffer?.symbol,
                    to: selectedOffer?.currency,
                    price: selectedOffer?.price || 0,
                    timeLeft: timeLeftForPriceCard,
                  }
                : undefined
            }
          />

          <div className="currencies-nav-page-danger-box">
            <img src={Alert} alt="Alert" />
            <span>{t("fxLimitText")}</span>
          </div>

          {!volumeValidation && (
            <div className="currencies-nav-page-info-box">
              <img src={Alert} alt="Alert" />
              <span>{t("orderVolumeOutOfRange")}</span>
            </div>
          )}

          <VerificationBtn
            onClick={() => handleReview()}
            text={t("reviewOrder")}
            disabled={
              !quantity ||
              // (limitPriceChecked && limitPriceInvalid) ||
              !volumeValidation
            }
          />
        </div>
      ) : activeTab === "trading" ? (
        <div key="trading" className="home-currency-section">
          <p className="home-currency-section_title">{t("currencies")}</p>

          <AssetsContainer
            customClick
            // onClick={() => {
            // setActiveTab("exchange");
            // }}
          />
        </div>
      ) : (
        <div key="orders" className="currencies-orders-section">
          <div className="currencies-orders-section-tabs">
            <button
              className={`
                currencies-orders-section-tabs-tab
                ${
                  activeOrderTab === "active"
                    ? "currencies-orders-section-tabs-tab-active"
                    : ""
                }
                `}
              onClick={() => setActiveOrderTab("active")}
            >
              <p>{t("activeOrders")}</p>
              <span></span>
            </button>

            <button
              className={`
                currencies-orders-section-tabs-tab
                ${
                  activeOrderTab === "history"
                    ? "currencies-orders-section-tabs-tab-active"
                    : ""
                }
                `}
              onClick={() => setActiveOrderTab("history")}
            >
              <p>{t("history")}</p>
              <span></span>
            </button>
          </div>

          <div
            key={activeOrderTab}
            className="currencies-orders-section-content"
          >
            {activeOrderTab === "active" ? (
              activeTransactionsCurrencies.length > 0 ? (
                activeTransactionsCurrencies.map((t: any, i) => (
                  <Transaction
                    key={i}
                    deal={t}
                    type="currency"
                    onSheetToggle={handleTransactionSheetToggle}
                  />
                ))
              ) : (
                <EmptyOrdersBox
                  title={t("noActiveOrders")}
                  description={t("noActiveFXOrdersMessage")}
                  buttonText={t("goToExchange")}
                  buttonOnClick={() => {
                    setActiveTab("exchange");
                  }}
                />
              )
            ) : transactionsCurrencies.length > 0 ? (
              transactionsCurrencies.map((t: any, i) => (
                <Transaction
                  key={i}
                  deal={t}
                  type="currency"
                  onSheetToggle={handleTransactionSheetToggle}
                />
              ))
            ) : (
              <EmptyOrdersBox
                title={t("noOrderHistory")}
                description={t("noFXOrdersHistoryMessage")}
                buttonText={t("goToExchange")}
                buttonOnClick={() => {
                  setActiveTab("exchange");
                }}
              />
            )}
          </div>
        </div>
      )}

      {infoModal && (
        <Modal
          onClick={() => {
            setInfoModal(false);
          }}
          onHide={() => {
            setInfoModal(false);
          }}
          title={t("Limit price")}
          text={t("setOwnPriceInfo")}
          showSecondBtn={false}
          customOutlet="customOutlet"
          btnText={t("close")}
          // btnText="Close"
        />
      )}
    </div>
  );
};

// <InfiniteScroll
//   dataLength={currItemsLength}
//   next={() => {
//     setPage(page + 1);
//   }}
//   hasMore={currItemsLength < count}
//   scrollThreshold={0.9}
//   scrollableTarget={"scrollable-container"}
//   loader={<p className="assets-loading">{t("loading")}</p>}
// >
// </InfiniteScroll>
